import React from 'react';
import { Alert, AlertProps } from '../Alert';

export interface SnackbarProps extends Omit<AlertProps, 'onClose'> {
  /** The number of milliseconds that this snackbar will show until it automatically disappears
   * @default 6000
   * */
  duration?: number;

  /**
   * @ignore
   * A function to call in order for the snackbar to remove itself. Should be provided through
   * the `SnackbarProvider` component
   * */
  destroy: () => void;

  /**
   * @ignore
   * The page position of the Snackbar (default is bottom left)
   * */
  position?:
    | 'top-left'
    | 'top-middle'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-middle'
    | 'bottom-right';

  /** Polymorphic as prop */
  as?: React.ElementType;
}

/**
 * A Snackbar is a special version of Alert that gets shown as a response to a user's action in
 * order to provide feedback about the outcome of their action
 */
export const Snackbar = ({ as, destroy, duration = 6000, ...rest }: SnackbarProps) => {
  const timeoutRef = React.useRef<NodeJS.Timeout | number>();

  React.useEffect(() => {
    timeoutRef.current = setTimeout(destroy, duration);
    return () => clearTimeout(timeoutRef.current as number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  // Makes snackbar polymorphic
  // always use the Alert as the base component
  const Element = as || Alert;
  return <Element {...rest} onClose={destroy} variantBackgroundStyle="solid" />;
};
